/**
 * Analytics utility — manages GA4 + internal event tracking
 * Respects cookie consent preferences stored in localStorage.
 */

const CONSENT_KEY = "ng_cookie_consent";
const GA_ID = "G-7M20ZTYGDZ";

type ConsentPreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

/** Read current consent from localStorage */
export function getConsent(): ConsentPreferences {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return { necessary: true, analytics: false, marketing: false };
    return JSON.parse(raw);
  } catch {
    return { necessary: true, analytics: false, marketing: false };
  }
}

/* ─── GA4 ─── */

let ga4Loaded = false;

/** Inject GA4 script only when analytics consent is granted */
export function initGA4() {
  const consent = getConsent();
  if (!consent.analytics || ga4Loaded) return;

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });

  ga4Loaded = true;
}

/** Send a GA4 event (only if loaded) */
function sendGA4Event(name: string, params?: Record<string, any>) {
  if (ga4Loaded && (window as any).gtag) {
    (window as any).gtag("event", name, params);
  }
}

/* ─── Internal events (Lovable Cloud) ─── */

import { supabase } from "@/integrations/supabase/client";

async function sendInternalEvent(name: string, properties?: Record<string, any>) {
  try {
    await (supabase.from("analytics_events" as any) as any).insert({
      event_name: name,
      event_properties: properties || {},
      page_url: window.location.pathname,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
    });
  } catch (e) {
    console.error("Analytics event error:", e);
  }
}

/* ─── Unified track function ─── */

/**
 * Track an event to all enabled analytics providers.
 * Always sends to internal DB; only sends to GA4 if consent is given.
 */
export function trackEvent(name: string, properties?: Record<string, any>) {
  // GA4 (consent-gated)
  sendGA4Event(name, properties);

  // PostHog (consent-gated)
  sendPostHogEvent(name, properties);

  // Internal (always — no cookies involved)
  sendInternalEvent(name, properties);
}

/* ─── PostHog ─── */

import posthog from "posthog-js";

const POSTHOG_KEY = "phc_XvIdHzzOOnMs3l6dOLoOHl5o0E9hT70n3q7JdfEcEMM";
const POSTHOG_HOST = "https://eu.i.posthog.com";

let posthogLoaded = false;

/** Initialize PostHog only when analytics consent is granted */
export function initPostHog() {
  const consent = getConsent();
  if (!consent.analytics || posthogLoaded) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    mask_all_text: false,
  });

  posthogLoaded = true;
}

/** Send a PostHog event (only if loaded) */
function sendPostHogEvent(name: string, params?: Record<string, any>) {
  if (posthogLoaded) {
    posthog.capture(name, params);
  }
}

/* ─── HubSpot ─── */

const HUBSPOT_PORTAL_ID = "48587480";
let hubspotLoaded = false;

/** Inject HubSpot tracking script only when marketing consent is granted */
export function initHubSpot() {
  const consent = getConsent();
  if (!consent.marketing || hubspotLoaded) return;

  const script = document.createElement("script");
  script.id = "hs-script-loader";
  script.src = `//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);

  hubspotLoaded = true;
}
